import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { NEW_SESSION_ID } from "@/features/forge/constants";
import { FileFullDetailsFragment } from "@/graphql/files.graphql";
import { convertInferenceParametersToFormValues } from "@/utils/convertInferenceParametersToFormValues";

import { SessionInferenceMode } from "../../components/graphql/schema.graphql";
import { InferenceFormNavigationState } from "../../components/inference-forms/constants";
import { canReforge, getWorkspaceForForge } from "../../features/files/utils";
import { SessionFileFragment } from "../../features/forge/forge-session/queries.graphql";
import { BasicDataWorkspaceFragment } from "../../hooks/queries.graphql";
import {
  is3DMimetype,
  isAudioMimetype,
  isImageMimetype,
  isVideoMimetype,
} from "../../utils/mimetype";
import { useRequiredWorkspace } from "../useRequiredWorkspace";
import useShowSnackbar from "../useShowSnackbar";

interface UseActivateReforgeFormProps {
  sessionId?: string;
  workspace?: BasicDataWorkspaceFragment;
}

export const useActivateReforgeForm = (props: UseActivateReforgeFormProps) => {
  const { sessionId, workspace: providedWorkspace } = props;
  const navigate = useNavigate();
  const { showSuccess, showError } = useShowSnackbar();

  const { userWorkspaces } = useRequiredWorkspace();

  const activateReforgeForm = useCallback(
    async (file: FileFullDetailsFragment | SessionFileFragment) => {
      let fileWorkspace = providedWorkspace;
      if (!!fileWorkspace && "workspace" in file && file.workspace != null) {
        fileWorkspace = file.workspace;
      }
      const workspaceForForge = getWorkspaceForForge({
        fileWorkspace,
        userWorkspaces,
      });

      if (!workspaceForForge) {
        showError("No workspace available to reforge this file.");
        return;
      }

      if (!canReforge(file)) {
        showError("Unable to reforge this generation.");
        return;
      }

      let activeMode: SessionInferenceMode | null = null;
      let formValues: Record<string, unknown> | null = null;

      if (file.styles?.length) {
        activeMode = file.styles[0].primaryInferenceMode;
      }
      const params = file?.inferenceParameters ?? {};
      if (isImageMimetype(file?.contentType)) {
        if (params.upscaleRatio) {
          activeMode = "UPSCALE";
        } else if (params.reframe) {
          activeMode = "REFRAME_IMAGE";
        } else if (params.vectorize) {
          activeMode = "VECTORIZE_IMAGE";
        } else if (params.removeBackground) {
          activeMode = "REMOVE_BACKGROUND";
        } else if (!activeMode) {
          activeMode = "CREATE";
        }
      } else if (isVideoMimetype(file?.contentType)) {
        if (params.upscaleRatio) {
          activeMode = "UPSCALE_VIDEO";
        } else if (params.reframe) {
          activeMode = "REFRAME_VIDEO";
        } else if (params.addSfxToVideo) {
          activeMode = "ADD_SFX_VIDEO";
        } else if (params.removeBackground) {
          activeMode = "REMOVE_BACKGROUND_VIDEO";
        } else if (!activeMode) {
          activeMode = "CREATE_VIDEO";
        }
      } else if (is3DMimetype(file?.contentType)) {
        if (params.retexture) {
          activeMode = "RETEXTURE_MESH";
        } else if (params.remesh) {
          activeMode = "REMESH";
        } else if (!activeMode) {
          activeMode = "CREATE_3_D";
        }
        // Mesh extraction is now handled by convertInferenceParametersToFormValues
        // which populates sourceMeshes from INIT_MESH files
      } else if (isAudioMimetype(file?.contentType)) {
        if (!activeMode) {
          activeMode = "CREATE_SPEECH";
        }
      }

      if (activeMode) {
        formValues = await convertInferenceParametersToFormValues(
          file.inferenceParameters ?? {},
          activeMode,
        );
      }

      if (!activeMode) {
        showError("Unable to determine reforge mode.");
        return;
      }

      const navSessionId = sessionId ?? file?.sessionId ?? NEW_SESSION_ID;
      const path = `/${workspaceForForge.name}/session/${navSessionId}`;

      navigate(path, {
        state: {
          form: {
            ...formValues,
          },
          mode: activeMode,
        } satisfies InferenceFormNavigationState,
      });

      showSuccess("Ready to re-forge with same parameters.", {
        messageId: "reforge-ready",
      });
    },
    [
      providedWorkspace,
      userWorkspaces,
      sessionId,
      showSuccess,
      showError,
      navigate,
    ],
  );

  return { activateReforgeForm };
};
